/**
 * Firebase Auth 기반 인증 및 사용자 세션 관리 Context
 *
 * [워크플로우]
 * 1. Firebase onAuthStateChanged로 인증 상태 감지
 * 2. Firebase ID Token 자동 관리 (getIdToken으로 갱신)
 * 3. 백엔드 API 호출하여 사용자 권한 검증 (/api/allowed-users/me)
 * 4. 세션 만료 시 자동 로그아웃
 *
 * [주요 기능]
 * - Firebase Google 로그인/로그아웃
 * - Firebase ID Token 자동 갱신 (Firebase SDK가 처리)
 * - 사용자 권한 관리 (isAdmin, isTenantAdmin, isTenantUser)
 * - 로컬 개발 모드 지원 (REACT_APP_DISABLE_AUTH=true)
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const isAuthDisabled = process.env.REACT_APP_DISABLE_AUTH === 'true' && process.env.NODE_ENV === 'development';

  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState(null);

  // Firebase onAuthStateChanged - 인증 상태 감지
  useEffect(() => {
    if (isAuthDisabled) {
      const mockUser = {
        email: 'dev@hyperlounge.com',
        displayName: 'Local Dev User',
        photoURL: null,
        uid: 'local-dev-mock'
      };
      const mockProfile = {
        email: 'dev@hyperlounge.com',
        displayName: 'Local Dev User',
        role: 'admin',
        isAdmin: true,
        permissions: ['*']
      };
      setCurrentUser(mockUser);
      setUserProfile(mockProfile);
      setIdToken('mock-token-dev');
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const user = {
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          uid: firebaseUser.uid
        };
        setCurrentUser(user);

        try {
          const token = await firebaseUser.getIdToken();
          setIdToken(token);
          await fetchUserProfile(token);
        } catch (error) {
          console.error('Failed to get Firebase ID token:', error);
          setCurrentUser(null);
          setIdToken(null);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setIdToken(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isAuthDisabled]);

  // Firebase ID Token 자동 갱신 (50분마다 - Firebase 토큰은 1시간 유효)
  useEffect(() => {
    if (!currentUser || isAuthDisabled) return;

    const refreshToken = async () => {
      try {
        const firebaseUser = auth.currentUser;
        if (firebaseUser) {
          const token = await firebaseUser.getIdToken(true);
          setIdToken(token);
        }
      } catch (error) {
        console.error('Token refresh error:', error);
      }
    };

    const interval = setInterval(refreshToken, 50 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser, isAuthDisabled]);

  // 백엔드에서 사용자 프로필 가져오기
  const fetchUserProfile = async (token) => {
    try {
      const response = await fetch(`${apiService.baseURL}/api/allowed-users/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.user);
      } else if (response.status === 403) {
        setUserProfile(null);
      }
    } catch (error) {
      console.error('Fetch user profile error:', error);
    }
  };

  // Firebase Google 로그인
  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const token = await result.user.getIdToken();
      setIdToken(token);

      const user = {
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        uid: result.user.uid
      };
      setCurrentUser(user);

      await fetchUserProfile(token);
      toast.success(`환영합니다, ${user.displayName}님!`);
      return user;
    } catch (error) {
      console.error('Google sign-in error:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        toast.error('로그인이 취소되었습니다.');
      } else {
        toast.error('Google 로그인에 실패했습니다.');
      }
      throw error;
    }
  };

  // Firebase 로그아웃
  const logout = async () => {
    try {
      await signOut(auth);
      setCurrentUser(null);
      setUserProfile(null);
      setIdToken(null);
      toast.success('로그아웃되었습니다.');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('로그아웃 중 오류가 발생했습니다.');
    }
  };

  // 프로필 업데이트
  const updateUserProfile = async (updates) => {
    try {
      if (!idToken) throw new Error('Not authenticated');

      const response = await fetch(`${apiService.baseURL}/api/allowed-users/profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.user);
        toast.success('프로필이 업데이트되었습니다.');
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('프로필 업데이트 중 오류가 발생했습니다.');
      throw error;
    }
  };

  // Permission check functions
  const hasPermission = (permission) => {
    if (!userProfile) return false;
    if (userProfile.permissions?.includes('*')) return true;
    return userProfile.permissions?.includes(permission) || false;
  };

  const isAdmin = () => {
    if (userProfile && (userProfile.role === 'admin' || userProfile.role === 'superadmin')) return true;
    if (userProfile && userProfile.isAdmin === true) return true;
    return false;
  };

  const isTenantAdmin = () => userProfile && userProfile.role === 'tenant_admin';
  const isTenantUser = () => userProfile && (userProfile.role === 'tenant_user' || userProfile.role === 'tenant_admin');
  const isSameTenant = (tenantId) => userProfile && userProfile.tenantId === tenantId;

  const getRouteType = () => {
    if (!userProfile) return null;
    if (isAdmin()) return 'admin';
    if (isTenantUser()) return 'tenant';
    return 'public';
  };

  // Legacy compatibility: handleGoogleSuccess wraps handleGoogleLogin
  const handleGoogleSuccess = async () => {
    return handleGoogleLogin();
  };

  const handleGoogleError = () => {
    toast.error('Google 로그인에 실패했습니다.');
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    idToken,
    handleGoogleLogin,
    handleGoogleSuccess,
    handleGoogleError,
    logout,
    updateUserProfile,

    hasPermission,
    isAdmin,
    isTenantAdmin,
    isTenantUser,
    isSameTenant,
    getRouteType,

    userRole: userProfile?.role,
    tenantId: userProfile?.tenantId,
    tenantName: userProfile?.tenantName,
    isAuthenticated: !!currentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
